import {useEffect, useRef, useCallback} from 'react'

import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {usePoNumber} from '@hconnect/tuc-processes/fields/textField'

/**
 * Hook that automatically updates the PO number field when a material is selected.
 * Uses the customerReference from the selected contract which contains the material's PO number.
 */
export const useMaterialPoNumber = () => {
  const {selectedMaterial, selectedContract} = useMix()
  const {poNumber, setPoNumber} = usePoNumber()
  const lastContractRef = useRef<string | null>(null)
  const currentPoNumberRef = useRef<string>('')

  // Update the ref whenever poNumber changes
  useEffect(() => {
    currentPoNumberRef.current = poNumber || ''
  }, [poNumber])

  const updatePoNumberFromContract = useCallback(() => {
    if (selectedMaterial?.poNumber) {
      // Check if this is a new contract selection
      // Only update if this is a different contract than the last one we processed
      if (lastContractRef.current !== selectedContract?.contractNumber) {
        lastContractRef.current = selectedContract?.contractNumber || ''

        // Only update if the current PO number is empty or still the default timestamp format
        const currentPoNumber = currentPoNumberRef.current
        const isDefaultPoNumber = !currentPoNumber || /^PO-\d{4}-\d{6}$/.test(currentPoNumber)

        if (isDefaultPoNumber) {
          setPoNumber(selectedMaterial.poNumber)
        }
      }
    } else {
      // Reset the ref when no contract is selected
      lastContractRef.current = null
    }
  }, [selectedContract?.contractNumber, selectedMaterial?.poNumber, setPoNumber])

  useEffect(() => {
    updatePoNumberFromContract()
  }, [updatePoNumberFromContract])

  return {
    poNumber,
    setPoNumber,
    selectedContract
  }
}
